import React, { useState } from 'react';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Popup from 'reactjs-popup';
import { Container, Col, Row } from 'react-bootstrap';
import balancedLightgapExample from '../assets/img/Balanced_Lightgap_Example.jpg';

import { useDealersByTerritory } from '../Store/entities/hooks';
import DealerAssign from './DealerAssign';
import TerritoryAssign from './TerritoryAssign';
import AddressSummaryBlock from './AddressSummaryBlock';
import ContractorAssign from './ContractorAssign';
import { useIsDealer } from '../hooks';
import { useQuoteById, useQuoteTypeNameByQuoteId } from '../Store/entities/quotes/hooks';
import { updateQuoteAddress, updateQuoteMeta } from '../Store/entities/quotes';
import { UsePortalDispatch } from '../Store';
import TextField from './NewOptionFields/TextField';
import AddressModal from './AddressModal';
import { fakeRootType } from '../FakeRootType';
import type { Address } from '../powershadesApiTypes';
import type { ExistingAddress } from './addressTypes';

/* eslint-disable camelcase */

type QuoteFieldsPropsType =
	{
		quoteId: number;
		ROOT: fakeRootType;
	};

const QuoteFields = ({ quoteId, ROOT }: QuoteFieldsPropsType) => {
	const dispatch = UsePortalDispatch();

	const {
		editable,
		territory_id: territoryId,
		property_address,
		shipping_address
	} = useQuoteById(Number(quoteId)) ?? {};

	const quote = useQuoteById(quoteId);

	const quoteType = useQuoteTypeNameByQuoteId(quoteId);
	const dealersInQuoteTerritory = useDealersByTerritory(territoryId ?? 0);

	const [showingPropertyAddressEditor, setShowingPropertyAddressEditor] = useState(false);
	const [showingShippingAddressEditor, setShowingShippingAddressEditor] = useState(false);
	const [prefillPropertyAddress, setPrefillPropertyAddress] = useState<ExistingAddress | null>(null);
	const [prefillShippingAddress, setPrefillShippingAddress] = useState<ExistingAddress | null>(null);

	const isManufacturer = false; // useIsManufacturer();

	const isDealer = useIsDealer();

	const changeTitle = (newValue) => {
		dispatch(updateQuoteMeta({
			quoteId,
			quoteVM: {
				title: newValue,
			}
			// onChange('title', newValue);
		}));
	};

	const changeReferenceNumber = (newValue) => {
		dispatch(updateQuoteMeta({
			quoteId,
			quoteVM: {
				reference_number: newValue,
			}
		}));
	};

	const isLightgapChoiceActive = quote.is_lightgap_choice_active === true;

	const showShippingEditable = editable;

	const getPrefillAddress = (
		sourceAddress: Address | undefined,
		targetType: 'property' | 'shipping',
		errorText: string,
	): ExistingAddress | null => {
		if (!sourceAddress) {
			ROOT.alert({
				quickFormat: 'error',
				text: errorText,
			});
			return null;
		}

		const prefill: ExistingAddress = {
			...sourceAddress,
		};

		delete prefill.id;
		delete prefill.address_id;
		delete prefill.snapshot_id;
		delete prefill.creation_date;

		if (targetType === 'shipping') {
			delete prefill.is_residential;
			delete prefill.lift_gate;
		}

		return prefill;
	};

	const hasFilledAddress = (address?: Address): boolean => {
		if (!address) return false;

		const candidates = [
			'address1', 'address_1', 'line1', 'street', 'street1',
			'city', 'state', 'postal_code', 'zip', 'zipcode',
		] as const;

		const addressAny = address as Record<string, unknown>;
		const hasCandidateValue = candidates.some((key) => {
			const v = addressAny[key];
			return typeof v === 'string' ? v.trim().length > 0 : false;
		});

		if (hasCandidateValue) return true;

		return Object.values(addressAny).some((v) => typeof v === 'string' && v.trim().length > 0);
	};

	const canCopyToInstallation = editable && hasFilledAddress(shipping_address);
	const canCopyToShipping = showShippingEditable && hasFilledAddress(property_address);

	return (
		<Row className="g-0">
			<Col xs={12} lg={3} className="inputs">
				<Row>
					<label htmlFor="title-input">
						Title
					</label>
					<TextField
						id="title-input"
						disabled={isManufacturer || !editable}
						value={quote.title ?? ""}
						onBlur={changeTitle}
						handleEnter={changeTitle}
					/>
				</Row>
				<Row>
					<label htmlFor="reference-number-input">
						Reference Number
					</label>
					<TextField
						id="reference-number-input"
						disabled={isManufacturer || !editable}
						value={quote.reference_number}
						onBlur={changeReferenceNumber}
						handleEnter={changeReferenceNumber}
					/>
				</Row>

				{quoteType !== 'BidSpec' ? (
					<TerritoryAssign
						isEditable={!isDealer && editable && quote.order_status === "active_quote"}
						quoteId={quoteId}
					/>
				) : null}

				{quoteType === 'BidSpec'
					|| isDealer
					|| !quoteId
					|| !territoryId
					|| !(dealersInQuoteTerritory)
					? null
					: (
						<DealerAssign
							key={quote.dealer_id}
							className="quote-field"
							quoteId={quoteId}
							extraMargin
							showDealerTier={false}
						/>
					)}
				{quoteType.toLocaleLowerCase() === 'bidspec' ? (
					<ContractorAssign
						key="contractor-assign"
						contractorId={0}
						setContractorId={(() => undefined)}
						editable={editable && quote.order_status === "active_quote"}
					/>
				) : null}
				{!isLightgapChoiceActive ? (
					<Row
						key="balanced-light-quote"
						className="mt-2"
					>
						<Col
							key="balanced-light-sentshience"
							xs={10}
						>
							<p>Light Gaps Default: Balanced</p>
						</Col>
						<Col
							className="text-end"
							key="balanced-light-icon"
							xs={2}
						>
							<p>
								<Popup
									trigger={
										(
											<span>
												<FontAwesomeIcon
													color="black"
													icon={faInfoCircle}
												/>
											</span>
										)
									}
									position="right center"
									keepTooltipInside
									on={['hover', 'focus']}
									arrow
								>
									<Container className="local-bootstrap">
										<Row>
											<Col>
												To enable lightgap manipulation, please go to
												Advanced Settings
											</Col>
											<br />
											<img
												alt="balanced-light-gap-example"
												className="hideonmobile"
												width={450}
												height={450}
												src={balancedLightgapExample}
											/>
										</Row>
									</Container>
								</Popup>
							</p>
						</Col>
					</Row>
				) : null}
			</Col>
			<Col sm={12} lg={4} className="address-summary-block">
				<AddressSummaryBlock
					title="Installation Address"
					address={property_address as ExistingAddress}
					showEdit={editable}
					showCopy={canCopyToInstallation}
					copyButtonText="Copy Shipping Address"
					onCopy={() => {
						const prefill = getPrefillAddress(
							shipping_address,
							'property',
							'There is no shipping address to copy from.',
						);

						if (prefill) {
							setPrefillPropertyAddress(prefill);
							setShowingPropertyAddressEditor(true);
						}
					}}
					onEdit={() => {
						setPrefillPropertyAddress(null);
						setShowingPropertyAddressEditor(true);
					}}
					quoteType={quoteType}
					isPropertyAddress
				/>
			</Col>
			<Col sm={12} lg={4} className="address-summary-block">
				<AddressSummaryBlock
					title="Shipping Address"
					address={shipping_address as ExistingAddress}
					showEdit={showShippingEditable}
					showCopy={canCopyToShipping}
					copyButtonText="Copy Installation Address"
					onCopy={() => {
						const prefill = getPrefillAddress(
							property_address,
							'shipping',
							'There is no installation address to copy from.',
						);

						if (prefill) {
							setPrefillShippingAddress(prefill);
							setShowingShippingAddressEditor(true);
						}
					}}
					onEdit={() => {
						setPrefillShippingAddress(null);
						setShowingShippingAddressEditor(true);
					}}
					quoteType={quoteType}
					isPropertyAddress={false}
				/>
			</Col>
			<>
				{showingPropertyAddressEditor ? (
					<AddressModal
						quoteType={quoteType}
						setAddress={(address) => dispatch(updateQuoteAddress({
							quoteId,
							address,
							addressType: "property"
						}))}
						onDone={() => setShowingPropertyAddressEditor(false)}
						addressLabel="Installation Address"
						address={property_address}
						getAddressesFunction="getMyPropertyAddresses"
						isPropertyAddress
						ROOT={ROOT}
						openEditorOnLoad={prefillPropertyAddress !== null}
						prefillAddress={prefillPropertyAddress}
					/>
				) : null}

				{showingShippingAddressEditor ? (
					<AddressModal
						quoteType={quoteType}
						setAddress={(address) => dispatch(updateQuoteAddress({
							quoteId,
							address,
							addressType: "shipping"
						}))}
						onDone={() => setShowingShippingAddressEditor(false)}
						addressLabel="Shipping Address"
						address={shipping_address}
						getAddressesFunction="getMyShippingAddresses"
						ROOT={ROOT}
						isPropertyAddress={false}
						openEditorOnLoad={prefillShippingAddress !== null}
						prefillAddress={prefillShippingAddress}
					/>
				) : null}
			</>
		</Row>
	);
};

export default QuoteFields;
